import Loading from "common/Loading";
import { Suspense, useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { closeSideBarDrawer, openSideBarDrawer } from "redux/Slice/sideBar";
import { RootState } from "redux/store";
import { SIDEBAR_BREAKPOINT } from "utils/constant";
import ScrollToTop from "utils/scrollToTop";
import useWindowDimensions from "utils/useWindowDimensions";
import { SideBar } from "./sideBar";
import "./style.css";
import TopBar from "./topBar";

const Layout = () => {
  // Hooks
  const sideBarRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();

  // Global States
  const { sideBar, auth } = useSelector(
    (state: RootState) => ({
      sideBar: state.sideBar,
      auth: state.auth,
    }),
    shallowEqual
  );

  // Local States
  const [sideBarWidth, setSideBarWidth] = useState(0);

  // Effects
  useEffect(() => {
    const handleKeyDown = (event: { key: string }) => {
      if (event.key === "Escape") {
        dispatch(closeSideBarDrawer());
      }
    };

    if (sideBar?.drawerVisibility) {
      document.body.classList.add("modal-open");
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sideBar?.drawerVisibility, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (sideBarRef?.current) {
        setSideBarWidth(sideBarRef?.current?.offsetWidth);
      }
    };

    handleResize();

    window?.addEventListener("resize", handleResize);

    return () => {
      window?.removeEventListener("resize", handleResize);
    };
  }, [sideBarRef]);

  // Components
  const CustomLoading = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100dvh",
        }}
      >
        <Loading />
      </div>
    );
  };

  return (
    <>
      <ScrollToTop />
      {auth?.isUserLoggedIn ? (
        <div>
          <div>
            {width <= SIDEBAR_BREAKPOINT ? (
              <Drawer
                open={sideBar?.drawerVisibility}
                onClose={() => dispatch(closeSideBarDrawer())}
                direction="left"
                style={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  height: "100dvh",
                  position: "fixed",
                }}
                size={sideBarWidth}
              >
                <SideBar reference={sideBarRef} />
              </Drawer>
            ) : (
              <SideBar reference={sideBarRef} />
            )}
          </div>
          <div>
            <div
              className="layoutSubContainer"
              style={{
                width:
                  width < SIDEBAR_BREAKPOINT
                    ? "100%"
                    : sideBar?.mode === "expanded"
                    ? "calc(100% - var(--side-bar-expanded-width))"
                    : "calc(100% - var(--side-bar-collapsed-width))",
              }}
            >
              {width <= SIDEBAR_BREAKPOINT ? (
                <TopBar onClick={() => dispatch(openSideBarDrawer())} />
              ) : null}
              <div
                className="layoutSubMainContainer"
                data-aos="fade"
                data-aos-delay="400"
              >
                <Suspense fallback={<CustomLoading />}>
                  <ScrollToTop />
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Navigate to={"/login"} />
      )}
    </>
  );
};

export default Layout;
